import { Form, Steps } from 'antd'
import { t } from 'i18next'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ModalBase, ModalContentContainer } from '../../../components'

import ProcessDeviationModalFooter from './footers/ProcessDeviationModalFooter'

import { useParams } from 'react-router-dom'
import { searchReviewSearchQueryResultItemStatus } from '../../../constants/constants'
import { useSearchReviews, useWorkspaces } from '../../../hooks'
import useProcessDeviation from '../../../hooks/useProcessDeviation'
import ProcessDeviationModalReviewStep from './steps/ProcessDeviationModalReviewStep'
import ProcessDeviationModalSetupStep from './steps/ProcessDeviationModalSetupStep'

interface ProcessDeviationModalProps {
  open: boolean
  onCancel: () => void
}

const ProcessDeviationModal = memo(
  ({ open, onCancel }: ProcessDeviationModalProps) => {
    const [form] = Form.useForm()
    const [currentStep, setCurrentStep] = useState(0)
    const [hasLoadedWorkspaces, setHasLoadedWorkspaces] = useState(false)
    const { searchReviewId, searchId } = useParams()

    const [isNextDisabled, setIsNextDisabled] = useState(false)
    const { selectedSearchQuery, selectedSearchReview } = useSearchReviews({})

    const { workspaceList, refreshWorkspaceList } = useWorkspaces({
      params: {
        dataSource: selectedSearchQuery?.searchParameters?.source?.toString(),
      },
    })
    const workspaceValueWatcher = Form.useWatch('workspace', form)

    const handleCancel = useCallback(() => {
      form.resetFields()
      setCurrentStep(0)
      onCancel()
    }, [form, onCancel])

    const { confirmLoading, addDeviationsToWorkspace } = useProcessDeviation({
      form,
      onSuccess: handleCancel,
    })

    useEffect(() => {
      if (
        open &&
        !hasLoadedWorkspaces &&
        selectedSearchQuery?.searchParameters?.source
      ) {
        refreshWorkspaceList()
        setHasLoadedWorkspaces(true)
      }
    }, [open, hasLoadedWorkspaces, refreshWorkspaceList, selectedSearchQuery])

    useEffect(() => {
      if (!open) {
        setHasLoadedWorkspaces(false)
      }
    }, [open])

    useEffect(() => {
      setIsNextDisabled(!workspaceValueWatcher)
    }, [workspaceValueWatcher])

    const includedResultsWithSummaries = useMemo(() => {
      return (
        selectedSearchQuery?.searchResults?.filter(
          (result) =>
            selectedSearchReview?.includedSearchResults?.includes(result.id) &&
            result.summaryStatus ===
              searchReviewSearchQueryResultItemStatus.READY
        ) || []
      )
    }, [
      selectedSearchQuery?.searchResults,
      selectedSearchReview?.includedSearchResults,
    ])

    const stepItems = [
      {
        title: t('processDeviationModal.steps.setup.title'),
      },
      {
        title: t('processDeviationModal.steps.review.title'),
      },
    ]

    const handleNext = useCallback(async () => {
      try {
        await form.validateFields()
        setCurrentStep(currentStep + 1)
      } catch (error) {
        console.error('Validation failed:', error)
      }
    }, [currentStep, form])

    const handleBack = () => {
      setCurrentStep(currentStep - 1)
    }

    const handleConfirm = useCallback(async () => {
      const searchQueryId = searchId
      if (!searchReviewId || !searchQueryId) {
        console.error('Missing searchReviewId or searchQueryId', {
          searchReviewId,
          searchQueryId,
        })
        return
      }

      await addDeviationsToWorkspace(
        searchReviewId,
        searchQueryId,
        includedResultsWithSummaries
      )
    }, [
      addDeviationsToWorkspace,
      includedResultsWithSummaries,
      searchId,
      searchReviewId,
    ])

    const steps = [
      <ProcessDeviationModalSetupStep
        form={form}
        workspaceList={workspaceList}
      />,
      <ProcessDeviationModalReviewStep
        form={form}
        selectedSearchQuery={selectedSearchQuery}
        includedResults={includedResultsWithSummaries}
      />,
    ]

    const footers = [
      <ProcessDeviationModalFooter
        onNext={handleNext}
        onCancel={handleCancel}
        disableNext={isNextDisabled}
        form={form}
      />,
      <ProcessDeviationModalFooter
        onSubmit={handleConfirm}
        onPrevious={handleBack}
        onCancel={handleCancel}
        form={form}
      />,
    ]

    return (
      <ModalBase
        title={t('processDeviationModal.title')}
        open={open}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        footer={footers[currentStep]}
      >
        <Steps size="small" current={currentStep} items={stepItems} />
        <Form form={form} layout="vertical">
          <ModalContentContainer>{steps[currentStep]}</ModalContentContainer>
        </Form>
      </ModalBase>
    )
  }
)

export default ProcessDeviationModal
